import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ExportPanel = ({ onExport }) => {
  const [exportFormat, setExportFormat] = useState('css');
  const [exportOptions, setExportOptions] = useState({
    includeColors: true,
    includeTypography: true,
    includeComponents: true,
    includeBrandAssets: true,
    includeDocumentation: true
  });

  const formatOptions = [
    { id: 'css', label: 'CSS Variables', icon: 'FileText', description: 'CSS custom properties' },
    { id: 'json', label: 'Design Tokens', icon: 'Braces', description: 'JSON token format' },
    { id: 'scss', label: 'SCSS Variables', icon: 'FileText', description: 'SCSS/Sass variables' },
    { id: 'js', label: 'JavaScript', icon: 'Code', description: 'JavaScript object' },
    { id: 'figma', label: 'Figma Tokens', icon: 'Figma', description: 'Figma design tokens' }
  ];

  const handleExportOptionChange = (option, value) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const handleExport = () => {
    const exportData = {
      format: exportFormat,
      options: exportOptions,
      timestamp: new Date().toISOString()
    };
    onExport(exportData);
  };

  const generatePreview = () => {
    switch (exportFormat) {
      case 'css':
        return `/* CLAB Design System - CSS Variables */
:root {
  --clab-primary: #2D9B9B;
  --clab-secondary: #1A7A7A;
  --clab-accent: #3DB8B8;
  --clab-success: #059669;
  --clab-warning: #d97706;
  --clab-error: #dc2626;
  
  --clab-font-heading: 'Inter', sans-serif;
  --clab-font-body: 'Inter', sans-serif;
  
  --clab-radius-sm: 0.25rem;
  --clab-radius-md: 0.375rem;
  --clab-radius-lg: 0.5rem;
}`;
      
      case 'json':
        return `{
  "brand": {
    "name": "CLAB",
    "tagline": "Construction & Engineering Biodata Management"
  },
  "colors": {
    "primary": "#2D9B9B",
    "secondary": "#1A7A7A",
    "accent": "#3DB8B8",
    "success": "#059669",
    "warning": "#d97706",
    "error": "#dc2626"
  },
  "typography": {
    "fontFamily": {
      "heading": "Inter",
      "body": "Inter"
    }
  }
}`;
      
      case 'scss':
        return `// CLAB Design System - SCSS Variables
$clab-primary: #2D9B9B;
$clab-secondary: #1A7A7A;
$clab-accent: #3DB8B8;
$clab-success: #059669;
$clab-warning: #d97706;
$clab-error: #dc2626;

$clab-font-heading: 'Inter', sans-serif;
$clab-font-body: 'Inter', sans-serif;

$clab-radius-sm: 0.25rem;
$clab-radius-md: 0.375rem;
$clab-radius-lg: 0.5rem;`;
      
      case 'js':
        return `// CLAB Design System - JavaScript
export const clabTheme = {
  brand: {
    name: 'CLAB',
    tagline: 'Construction & Engineering Biodata Management'
  },
  colors: {
    primary: '#2D9B9B',
    secondary: '#1A7A7A',
    accent: '#3DB8B8',
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626'
  },
  typography: {
    fontFamily: {
      heading: 'Inter',
      body: 'Inter'
    }
  }
};`;
      
      case 'figma':
        return `{
  "global": {
    "Brand/Primary": {
      "value": "#2D9B9B",
      "type": "color"
    },
    "Brand/Secondary": {
      "value": "#1A7A7A",
      "type": "color"
    },
    "Typography/Heading": {
      "value": "Inter",
      "type": "fontFamilies"
    }
  }
}`;
      
      default:
        return '';
    }
  };

  return (
    <div className="bg-surface border border-secondary-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Export Theme</h3>
        <Icon name="Download" size={20} className="text-[#2D9B9B]" />
      </div>

      {/* Export Format Selection */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-text-primary mb-3">Export Format</h4>
        <div className="space-y-2">
          {formatOptions.map((format) => (
            <label key={format.id} className="flex items-center space-x-3 p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50 cursor-pointer transition-colors">
              <input
                type="radio"
                name="exportFormat"
                value={format.id}
                checked={exportFormat === format.id}
                onChange={(e) => setExportFormat(e.target.value)}
                className="text-[#2D9B9B] focus:ring-[#2D9B9B]"
              />
              <Icon name={format.icon} size={16} className="text-text-secondary" />
              <div className="flex-1">
                <div className="font-medium text-text-primary">{format.label}</div>
                <div className="text-sm text-text-secondary">{format.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Export Options */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-text-primary mb-3">Include in Export</h4>
        <div className="space-y-3">
          {[
            { key: 'includeColors', label: 'Color Palette', description: 'All theme colors and variations' },
            { key: 'includeTypography', label: 'Typography', description: 'Font families and text styles' },
            { key: 'includeComponents', label: 'Component Styles', description: 'Button, card, and form styles' },
            { key: 'includeBrandAssets', label: 'Brand Assets', description: 'Logo and brand information' },
            { key: 'includeDocumentation', label: 'Documentation', description: 'Usage guidelines and examples' }
          ].map((option) => (
            <label key={option.key} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportOptions[option.key]}
                onChange={(e) => handleExportOptionChange(option.key, e.target.checked)}
                className="mt-1 text-[#2D9B9B] focus:ring-[#2D9B9B]"
              />
              <div className="flex-1">
                <div className="font-medium text-text-primary">{option.label}</div>
                <div className="text-sm text-text-secondary">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-text-primary mb-3">Preview</h4>
        <div className="bg-secondary-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-secondary-200 whitespace-pre-wrap">
            <code>{generatePreview()}</code>
          </pre>
        </div>
      </div>

      {/* Export Actions */}
      <div className="space-y-3">
        <button
          onClick={handleExport}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] text-white rounded-lg hover:from-[#1A7A7A] hover:to-[#2D9B9B] transition-all duration-200 font-medium"
        >
          <Icon name="Download" size={16} />
          <span>Export Theme</span>
        </button>
        
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigator.clipboard.writeText(generatePreview())}
            className="flex items-center justify-center space-x-2 px-4 py-2 border border-secondary-300 text-text-secondary rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <Icon name="Copy" size={14} />
            <span>Copy Code</span>
          </button>
          
          <button
            onClick={() => {
              const blob = new Blob([generatePreview()], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `clab-theme.${exportFormat}`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center justify-center space-x-2 px-4 py-2 border border-secondary-300 text-text-secondary rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <Icon name="Save" size={14} />
            <span>Save File</span>
          </button>
        </div>
      </div>

      {/* Export Info */}
      <div className="mt-6 pt-4 border-t border-secondary-200">
        <div className="flex items-start space-x-3 p-3 bg-[#2D9B9B]/5 rounded-lg">
          <Icon name="Info" size={16} className="text-[#2D9B9B] mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-text-primary font-medium mb-1">Export Information</p>
            <p className="text-text-secondary">
              Exported files will include all selected components and follow industry-standard formats for easy integration into your development workflow.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;